import {useMutation, useQuery} from "@apollo/client";
import {ProfileTdas} from "../../graphql/queries/profileTdas";
import _ from "lodash";
import {ProfileData} from "../../graphql/queries/profileData";
import {UpdateProfile} from "../../graphql/mutations/updateProfile";
import {RequestAccountDeletion} from "../../graphql/mutations/requestAccountDeletion";
import React from "react";
import {LoadingScreen, TDAForm, BasicModalContent} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import {Box, Grid, Typography, useTheme} from "@mui/joy";
import {IconButton, Button, Dialog, CircularProgress} from "@mui/material";
import useMediaQuery from "@mui/system/useMediaQuery";
import {EditRounded, CloseRounded, DeleteRounded} from '@mui-symbols-material/w200';
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import moment from "moment";
import {useKeycloak} from "@react-keycloak/web";

interface KeyValueProps {
    title: string,
    value?: string | React.ReactNode,
}

export const KeyValueInformation = ({title, value}: KeyValueProps) => {
    return <Grid container width="100%" justifyContent="space-between" sx={{pb: "12px"}}>
        <Grid xs={12} md={4}>
            <Typography level="body-md" sx={{maxWidth: "200px"}} color="neutral">
                {title}:
            </Typography>
        </Grid>
        <Grid xs={12} md={8}>
            {
                !value || typeof value === "string" ?
                    <Typography level="body-md" fontWeight="lg" sx={{wordBreak: "break-word", color: "#333" }}>
                        {value || "—"}
                    </Typography>
                    :
                    value
            }
        </Grid>
    </Grid>
}

const ProfileInformation = () => {
    const {t} = useTranslation();
    const {keycloak} = useKeycloak();
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const profileTdaQuery = useQuery(ProfileTdas);
    const tdas = _.get(profileTdaQuery, 'data.profile.typeDefinitionAttributeList.typeDefinitionAttributes', []);

    const profileDataQuery = useQuery(ProfileData);
    const profileData = _.get(profileDataQuery, 'data.profile', {});
    const deletionRequestedAt = profileData.deletionRequestedAt;

    const translateLanguage = (lang: string) => {
        switch (lang) {
            case 'en':
                return t("English");
            case 'de':
                return t("German");
            case 'fr':
                return t("French");
            case 'ko':
                return t("Korean");
            case 'zh':
                return t("Chinese");
            default:
                return lang;
        }
    }

    const translateMeasurementSystem = (measurementSystem: string) => {
        switch (measurementSystem) {
            case 'SI':
                return t("SI – International System of Units");
            case 'IMPERIAL':
                return t("Imperial / US Customary System");
            default:
                return measurementSystem;
        }
    }

    const [updateProfileMutation] = useMutation(UpdateProfile);
    const [requestAccountDeletionMutation] = useMutation(RequestAccountDeletion);

    const onUpdateProfile = (values: {[p: string]: any}) => {
        return updateProfileMutation({
            variables: {
                input: values
            },
            refetchQueries: ["ProfileData"]
        }).then(async (res) => {
            const newLanguage = res.data?.updateProfile?.language;
            if (newLanguage && newLanguage !== sessionStorage.getItem("lng")) {
                sessionStorage.setItem("lng", newLanguage);
                try {
                    await keycloak.updateToken(-1);
                } catch (e) {
                    console.warn("[Keycloak] Failed to refresh token after language change", e);
                }
                // Server-localized data (Apollo cache, TDA labels, Novu inbox) only
                // switches on a fresh load, so reload instead of i18n.changeLanguage.
                window.location.reload();
                return;
            }
            toast.success(t("Update successful."));
        }).catch(err => {
            toast.error(resolveErrorMessage(err, t))
        }).finally(() => {
            setFormVisible(false);
        })
    }

    const [formVisible, setFormVisible] = React.useState(false);
    const showUpdateProfileForm= () => {
        setFormVisible(!formVisible);
    }

    const [hovered, setHovered] = React.useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);

    const handleDeleteAccount = () => {
        setIsDeleting(true);
        requestAccountDeletionMutation({
            refetchQueries: ["ProfileData"]
        })
            .then(res => {
                const result = _.get(res, 'data.requestAccountDeletion');
                if (result?.success) {
                    toast.success(result.message || t("Account deletion request submitted successfully."));
                } else {
                    toast.error(result?.message || t("Account deletion request failed."));
                }
            })
            .catch(err => {
                toast.error(resolveErrorMessage(err, t));
            })
            .finally(() => {
                setIsDeleting(false);
                setDeleteModalOpen(false);
            });
    }

    if (profileTdaQuery.loading || profileDataQuery.loading) {
        return <LoadingScreen />;
    }
    return (
        <Box sx={{padding: "12px 36px", width: "100%", maxWidth: "720px", overflowY: "auto", minHeight: 0}}
             onMouseEnter={() => setHovered(true)}
             onMouseLeave={() => setHovered(false)}>

            <Box sx={{display: "flex", justifyContent: "space-between", marginBottom: "12px", maxWidth: "720px", borderBottom: "1px solid #ccc"}}>
                <Typography level={"titleLarge"} sx={{ paddingBottom: "8px" }}>
                    {t("Profile Details")}
                </Typography>
                {hovered && (
                    <IconButton onClick={showUpdateProfileForm}>
                        {formVisible ? <CloseRounded /> : <EditRounded />}
                    </IconButton>
                )}
            </Box>


            {!formVisible && (
                <Box>
                    <KeyValueInformation title={t("Name")} value={`${profileData.firstName} ${profileData.lastName}`}/>
                    <KeyValueInformation title={t("Email")} value={profileData.email}/>
                    <KeyValueInformation title={t("Phone")} value={profileData.phone || t("Not provided")}/>
                    <KeyValueInformation title={t("Language")} value={translateLanguage(profileData.language)}/>
                    <KeyValueInformation title={t("Timezone")} value={profileData.timezone}/>
                    <KeyValueInformation title={t("Measurement System")} value={translateMeasurementSystem(profileData.measurementSystem)}/>
                    <KeyValueInformation
                        title={t("Roles")}
                        value={profileData.roles && profileData.roles.length > 0
                            ? profileData.roles
                                .map((role: any) => `${role.name}`)
                                .join(', ')
                            : t("No roles assigned")}/>
                </Box>
            )}
            {formVisible && (
                <Box>
                    <TDAForm attributes={tdas} onSubmit={onUpdateProfile} disableForm={false} />
                </Box>
            )}

            {!formVisible && (
                <Box sx={{ mt: 4 }}>
                    <Box sx={{ marginBottom: "12px", borderBottom: "1px solid #ccc" }}>
                        <Typography level={"titleLarge"} sx={{ paddingBottom: "8px" }}>
                            {t("Delete account")}
                        </Typography>
                    </Box>
                    {deletionRequestedAt ? (
                        <>
                            <Typography level="body-md" sx={{ mb: 2 }}>
                                {t("You have already requested account deletion on {{date}}.", {date: moment(deletionRequestedAt).format('DD.MM.YYYY')})}
                            </Typography>
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<DeleteRounded />}
                                disabled
                            >
                                {t("Deletion requested")}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Typography level="body-md" sx={{ mb: 2 }}>
                                {t("You can request to have your account permanently deleted. This action cannot be undone.")}
                            </Typography>
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<DeleteRounded />}
                                onClick={() => setDeleteModalOpen(true)}
                            >
                                {t("Request deletion")}
                            </Button>
                        </>
                    )}
                </Box>
            )}

            <Dialog
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                fullScreen={isFullScreen}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: isFullScreen ? undefined : "500px",
                        },
                    },
                }}
            >
                <BasicModalContent
                    startIcon={<DeleteRounded />}
                    onClose={() => setDeleteModalOpen(false)}
                    title={t("Delete account")}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <Typography level="body-md" sx={{ mt: 1 }}>
                            {t("Are you sure you want to request the deletion of your account? This action cannot be undone.")}
                        </Typography>

                        <Grid container spacing={1} sx={{ pt: "8px" }}>
                            <Grid xs={6}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => setDeleteModalOpen(false)}
                                    disabled={isDeleting}
                                >
                                    {t("Cancel")}
                                </Button>
                            </Grid>
                            <Grid xs={6}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    fullWidth
                                    onClick={handleDeleteAccount}
                                    disabled={isDeleting}
                                    startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteRounded />}
                                >
                                    {isDeleting ? t("Requesting...") : t("Request deletion")}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </BasicModalContent>
            </Dialog>
        </Box>
)}
export default ProfileInformation;
